import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faClock, faBolt, faDatabase, faWifi, faMicrochip, faBrain, faLightbulb, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import LoadingScreen from './LoadingScreen';
import apiService from '../services/api';
import { useLocation } from 'react-router-dom';

const Metrics = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [expandedQuery, setExpandedQuery] = useState(null);
  const [liveData, setLiveData] = useState({
    cogIOThroughput: 340,
    cogCacheHit: 76,
    xarrayChunkSize: 1024,
    xarraySpillPercent: 12,
    daskWorkers: 8,
    daskIdleWorkers: 2,
    daskTaskRuntime: 142,
    postgisIntersectsPerSec: 245,
    postgisSlowQueries: 12,
    postgisIndexBloat: 8,
    geoserverWMSErrorRate: 0.2,
    geoserverCacheHit: 89,
    geoserverSlowLayers: 2,
    currentTime: new Date(),
    queueByInstrument: {
      'Sentinel-2': 24,
      'Landsat-8': 12,
      'MODIS': 8,
      'Planet': 6
    },
    stacItemsIndexed: 12847,
    stacInvalidItems: 23
  });

  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    // Initial load
    const loadingTime = 1000 + Math.random() * 1500;
    setTimeout(async () => {
      try {
        const data = await apiService.getLiveMetrics();
        setLiveData({ ...data, currentTime: new Date() });
      } catch (error) {
        console.error('Failed to load live metrics:', error);
      }
      setLoading(false);

      // Scroll to slow-gis-queries if hash is present
      setTimeout(() => {
        if (window.location.hash === '#slow-gis-queries') {
          const element = document.getElementById('slow-gis-queries');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 100);
    }, loadingTime);
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const data = await apiService.getLiveMetrics();
        setLiveData({ ...data, currentTime: new Date() });
        setLastUpdate(new Date());
      } catch (error) {
        console.error('Failed to update live metrics:', error);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="metrics-container">
        <LoadingScreen message="Loading live metrics..." />
      </div>
    );
  }

  return (
    <div className="metrics-container">
      <div className="metrics-header">
        <h2>
          <FontAwesomeIcon icon={faEye} style={{ marginRight: '0.5rem' }} />
          Live System Metrics
        </h2>
        <div className="live-indicator">
          <div className="live-dot live"></div>
          <span className="live-text">
            LIVE • Last update: {lastUpdate.toLocaleTimeString()}
          </span>
        </div>
      </div>

      <div className="metrics-content">
        <div className="metrics-grid">
          <div className="metric-card primary">
            <div className="metric-icon">
              <FontAwesomeIcon icon={faBolt} />
            </div>
            <div className="metric-content">
              <div className="metric-value">
                {liveData.cogIOThroughput.toFixed(0)} MB/s
              </div>
              <div className="metric-label">COG I/O Throughput</div>
              <div className="metric-change positive">Cache hit: {liveData.cogCacheHit.toFixed(1)}%</div>
            </div>
          </div>

          <div className="metric-card secondary">
            <div className="metric-icon">
              <FontAwesomeIcon icon={faDatabase} />
            </div>
            <div className="metric-content">
              <div className="metric-value">
                {liveData.xarrayChunkSize} x {liveData.xarrayChunkSize}
              </div>
              <div className="metric-label">Xarray Chunk Size</div>
              <div className="metric-change warning">Spill: {liveData.xarraySpillPercent.toFixed(1)}%</div>
            </div>
          </div>

          <div className="metric-card accent">
            <div className="metric-icon">
              <FontAwesomeIcon icon={faMicrochip} />
            </div>
            <div className="metric-content">
              <div className="metric-value">
                {liveData.daskWorkers - liveData.daskIdleWorkers} / {liveData.daskWorkers}
              </div>
              <div className="metric-label">Dask Workers (Active)</div>
              <div className="metric-change neutral">Avg task: {liveData.daskTaskRuntime}ms</div>
            </div>
          </div>

          <div className="metric-card warning">
            <div className="metric-icon">
              <FontAwesomeIcon icon={faDatabase} />
            </div>
            <div className="metric-content">
              <div className="metric-value">
                {liveData.postgisIntersectsPerSec}/sec
              </div>
              <div className="metric-label">PostGIS ST_Intersects</div>
              <div className="metric-change negative">{liveData.postgisSlowQueries} slow queries</div>
            </div>
          </div>

          <div className="metric-card info">
            <div className="metric-icon">
              <FontAwesomeIcon icon={faWifi} />
            </div>
            <div className="metric-content">
              <div className="metric-value">
                {liveData.geoserverWMSErrorRate.toFixed(1)}%
              </div>
              <div className="metric-label">GeoServer WMS Error Rate</div>
              <div className="metric-change positive">Cache: {liveData.geoserverCacheHit.toFixed(1)}%</div>
            </div>
          </div>

          <div className="metric-card neutral">
            <div className="metric-icon">
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div className="metric-content">
              <div className="metric-value">
                {liveData.stacItemsIndexed.toLocaleString()}
              </div>
              <div className="metric-label">STAC Items Indexed</div>
              <div className="metric-change warning">{liveData.stacInvalidItems} invalid</div>
            </div>
          </div>
        </div>

        <section className="dashboard-section">
          <h3>Ingestion Queue by Mission</h3>
          <div className="status-grid">
            <div className="status-item" style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <div className="status-indicator status-healthy"></div>
                <span className="status-label">Sentinel-2</span>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                <span className="status-value">{liveData.queueByInstrument['Sentinel-2']} pending</span>
                <span style={{fontSize: '0.75rem', color: '#888'}}>/ 24 expected</span>
              </div>
              <span style={{fontSize: '0.7rem', color: '#10b981'}}>✓ On schedule (NRT priority)</span>
            </div>
            <div className="status-item" style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <div className="status-indicator status-warning"></div>
                <span className="status-label">Landsat-8</span>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                <span className="status-value">{liveData.queueByInstrument['Landsat-8']} pending</span>
                <span style={{fontSize: '0.75rem', color: '#888'}}>/ 12 expected</span>
              </div>
              <span style={{fontSize: '0.7rem', color: '#f59e0b'}}>⚠ 2h backlog (Backfill)</span>
            </div>
            <div className="status-item" style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <div className="status-indicator status-healthy"></div>
                <span className="status-label">MODIS</span>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                <span className="status-value">{liveData.queueByInstrument['MODIS']} pending</span>
                <span style={{fontSize: '0.75rem', color: '#888'}}>/ 8 expected</span>
              </div>
              <span style={{fontSize: '0.7rem', color: '#10b981'}}>✓ On schedule (Daily)</span>
            </div>
            <div className="status-item" style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <div className="status-indicator status-healthy"></div>
                <span className="status-label">Planet</span>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                <span className="status-value">{liveData.queueByInstrument['Planet']} pending</span>
                <span style={{fontSize: '0.75rem', color: '#888'}}>/ 15 expected</span>
              </div>
              <span style={{fontSize: '0.7rem', color: '#10b981'}}>✓ On schedule (Commercial)</span>
            </div>
          </div>

          {/* Pipeline Worker Backlog */}
          <div style={{marginTop: '1.5rem', padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <div>
                <div style={{fontSize: '0.85rem', color: '#888', marginBottom: '0.25rem'}}>Pipeline Worker Backlog</div>
                <div style={{fontSize: '1.5rem', fontWeight: 600, color: '#3b82f6'}}>120 tasks</div>
              </div>
              <div style={{textAlign: 'right'}}>
                <div style={{fontSize: '0.75rem', color: '#888'}}>Dask Workers: 8 active</div>
                <div style={{fontSize: '0.75rem', color: '#888'}}>Avg wait time: 4.2 min</div>
              </div>
            </div>
          </div>
        </section>

        <section className="dashboard-section" id="slow-gis-queries">
          <h3>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem', color: '#f59e0b' }} />
            Slow GIS Queries
          </h3>
          <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1rem' }}>
            Queries taking longer than 1000ms. Click to expand for AI analysis and optimization suggestions.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {/* Query 1 */}
            <div
              style={{
                padding: '1rem',
                background: expandedQuery === 1 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                border: expandedQuery === 1 ? '1px solid #f59e0b' : '1px solid rgba(239, 68, 68, 0.3)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setExpandedQuery(expandedQuery === 1 ? null : 1)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#ef4444', marginBottom: '0.25rem' }}>
                    ST_Intersects on parcels table
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    Execution time: <strong style={{ color: '#ef4444' }}>2347ms</strong>
                  </div>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#888' }}>
                  {expandedQuery === 1 ? '▼' : '▶'}
                </div>
              </div>

              {expandedQuery === 1 && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: '#94a3b8' }}>Query:</strong>
                    <pre style={{
                      background: 'rgba(0,0,0,0.3)',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      overflow: 'auto',
                      marginTop: '0.5rem'
                    }}>
{`SELECT COUNT(*)
FROM parcels
WHERE ST_Intersects(
  geom,
  ST_MakeEnvelope(23.5, 37.8, 24.2, 38.3, 4326)
);`}
                    </pre>
                  </div>

                  <div style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    padding: '1rem',
                    borderRadius: '6px',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <FontAwesomeIcon icon={faBrain} style={{ color: '#3b82f6' }} />
                      <strong style={{ color: '#3b82f6' }}>AI Root Cause Analysis</strong>
                    </div>
                    <p style={{ fontSize: '0.85rem', lineHeight: '1.5', margin: 0 }}>
                      Missing spatial index on geometry column. PostGIS is performing a full table scan across {(Math.random() * 500000 + 100000).toFixed(0)} parcels without GIST index optimization.
                    </p>
                  </div>

                  <div style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    padding: '1rem',
                    borderRadius: '6px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <FontAwesomeIcon icon={faLightbulb} style={{ color: '#10b981' }} />
                      <strong style={{ color: '#10b981' }}>AI Optimization Suggestion</strong>
                    </div>
                    <p style={{ fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '0.75rem' }}>
                      Add spatial index to enable GIST-based spatial queries:
                    </p>
                    <pre style={{
                      background: 'rgba(0,0,0,0.4)',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      overflow: 'auto'
                    }}>
{`CREATE INDEX idx_parcels_geom
ON parcels USING GIST(geom);

VACUUM ANALYZE parcels;`}
                    </pre>
                    <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.5rem', marginBottom: 0 }}>
                      Expected speedup: <strong style={{ color: '#10b981' }}>~200x faster</strong> (2347ms → ~12ms)
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Query 2 */}
            <div
              style={{
                padding: '1rem',
                background: expandedQuery === 2 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                border: expandedQuery === 2 ? '1px solid #f59e0b' : '1px solid rgba(239, 68, 68, 0.3)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setExpandedQuery(expandedQuery === 2 ? null : 2)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#ef4444', marginBottom: '0.25rem' }}>
                    ST_Union on raster tiles
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    Execution time: <strong style={{ color: '#ef4444' }}>1823ms</strong>
                  </div>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#888' }}>
                  {expandedQuery === 2 ? '▼' : '▶'}
                </div>
              </div>

              {expandedQuery === 2 && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: '#94a3b8' }}>Query:</strong>
                    <pre style={{
                      background: 'rgba(0,0,0,0.3)',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      overflow: 'auto',
                      marginTop: '0.5rem'
                    }}>
{`SELECT ST_Union(rast)
FROM sentinel2_tiles
WHERE tile_id IN ('34SGA', '34SGC', '34SGB')
  AND acquisition_date = '2025-10-02';`}
                    </pre>
                  </div>

                  <div style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    padding: '1rem',
                    borderRadius: '6px',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <FontAwesomeIcon icon={faBrain} style={{ color: '#3b82f6' }} />
                      <strong style={{ color: '#3b82f6' }}>AI Root Cause Analysis</strong>
                    </div>
                    <p style={{ fontSize: '0.85rem', lineHeight: '1.5', margin: 0 }}>
                      Raster union operation on large tiles without pre-aggregation. Each tile is ~250MB uncompressed, causing memory-bound operation.
                    </p>
                  </div>

                  <div style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    padding: '1rem',
                    borderRadius: '6px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <FontAwesomeIcon icon={faLightbulb} style={{ color: '#10b981' }} />
                      <strong style={{ color: '#10b981' }}>AI Optimization Suggestion</strong>
                    </div>
                    <p style={{ fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '0.75rem' }}>
                      Use materialized views or pre-compute mosaics for common AOIs. Alternatively, add raster constraints:
                    </p>
                    <pre style={{
                      background: 'rgba(0,0,0,0.4)',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      overflow: 'auto'
                    }}>
{`-- Create index on raster column
CREATE INDEX idx_s2_tiles_rast
ON sentinel2_tiles USING GIST(ST_ConvexHull(rast));

-- Or use out-of-db raster with GDAL VRT
SELECT ST_Union(ST_Clip(rast, AOI_geom)) ...`}
                    </pre>
                    <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.5rem', marginBottom: 0 }}>
                      Expected speedup: <strong style={{ color: '#10b981' }}>~5x faster</strong> (1823ms → ~350ms)
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Query 3 */}
            <div
              style={{
                padding: '1rem',
                background: expandedQuery === 3 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
                borderRadius: '8px',
                border: expandedQuery === 3 ? '1px solid #f59e0b' : '1px solid rgba(245, 158, 11, 0.3)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setExpandedQuery(expandedQuery === 3 ? null : 3)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#f59e0b', marginBottom: '0.25rem' }}>
                    ST_Within on buildings layer
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    Execution time: <strong style={{ color: '#f59e0b' }}>1245ms</strong>
                  </div>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#888' }}>
                  {expandedQuery === 3 ? '▼' : '▶'}
                </div>
              </div>

              {expandedQuery === 3 && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: '#94a3b8' }}>Query:</strong>
                    <pre style={{
                      background: 'rgba(0,0,0,0.3)',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      overflow: 'auto',
                      marginTop: '0.5rem'
                    }}>
{`SELECT b.*
FROM buildings b, municipalities m
WHERE ST_Within(b.geom, m.geom)
  AND m.name = 'Athens';`}
                    </pre>
                  </div>

                  <div style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    padding: '1rem',
                    borderRadius: '6px',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <FontAwesomeIcon icon={faBrain} style={{ color: '#3b82f6' }} />
                      <strong style={{ color: '#3b82f6' }}>AI Root Cause Analysis</strong>
                    </div>
                    <p style={{ fontSize: '0.85rem', lineHeight: '1.5', margin: 0 }}>
                      Cartesian join (cross product) between buildings and municipalities before spatial filter. PostgreSQL query planner not using spatial index efficiently.
                    </p>
                  </div>

                  <div style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    padding: '1rem',
                    borderRadius: '6px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <FontAwesomeIcon icon={faLightbulb} style={{ color: '#10b981' }} />
                      <strong style={{ color: '#10b981' }}>AI Optimization Suggestion</strong>
                    </div>
                    <p style={{ fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '0.75rem' }}>
                      Rewrite query to use subquery or CTE for spatial filtering:
                    </p>
                    <pre style={{
                      background: 'rgba(0,0,0,0.4)',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      overflow: 'auto'
                    }}>
{`SELECT b.*
FROM buildings b
WHERE ST_Within(
  b.geom,
  (SELECT geom FROM municipalities WHERE name = 'Athens')
);

-- Also ensure indexes exist:
CREATE INDEX idx_buildings_geom ON buildings USING GIST(geom);
CREATE INDEX idx_municipalities_geom ON municipalities USING GIST(geom);`}
                    </pre>
                    <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.5rem', marginBottom: 0 }}>
                      Expected speedup: <strong style={{ color: '#10b981' }}>~10x faster</strong> (1245ms → ~120ms)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Metrics;